import React, { useState, useEffect, useRef } from "react";
import { Button, Input, CircularProgress, Chip } from "@nextui-org/react";
import { BsTelephoneX } from "react-icons/bs";
import { LuPlus, LuPhoneOff } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import myAxios from "../../utils/axiosWrapper";

const BlacklistPanel = ({ apiUrl, onDirtyChange = () => {} }) => {
  const { t } = useTranslation();

  const [numbers, setNumbers] = useState([]);
  const [newNumber, setNewNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialSnapshotRef = useRef("[]");

  const isDirty = JSON.stringify(numbers) !== initialSnapshotRef.current;

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty]);

  const fetchBlacklistedNumbers = async () => {
    setIsLoading(true);
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/admin/get_company_blacklisted_numbers`
      );
      const data = response?.data?.[0] || {};
      const retrievedNumbers = data.blacklisted_numbers || [];
      setNumbers(retrievedNumbers);
      initialSnapshotRef.current = JSON.stringify(retrievedNumbers);
    } catch (error) {
      console.error("Error fetching blacklisted numbers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklistedNumbers();
  }, []);

  const handleAddNumber = () => {
    const trimmed = newNumber.trim();
    if (!trimmed || numbers.includes(trimmed)) return;
    setNumbers([...numbers, trimmed]);
    setNewNumber("");
  };

  const handleRemoveNumber = (number) => {
    setNumbers(numbers.filter((n) => n !== number));
  };

  const handleCancel = () => {
    setNumbers(JSON.parse(initialSnapshotRef.current));
    setNewNumber("");
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await myAxios.put(`${apiUrl}/audio/api/v1/admin/update_company_blacklisted_numbers`, {
        blacklisted_numbers: numbers,
      });
      await fetchBlacklistedNumbers();
    } catch (error) {
      console.error("Error saving blacklisted numbers:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-w-[800px]">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <LuPhoneOff
            size={20}
            className="text-primary"
          />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[#171717]">{t("Numeros blacklistes")}</h2>
          <p className="text-xs text-[#737373]">
            {numbers.length} {t(numbers.length > 1 ? "numeros" : "numero")}
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-4">
        {t(
          "Les appels provenant de ces numeros sont exclus de l'analyse. Les numeros sont compares exactement, sans normalisation."
        )}
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="flex gap-2 items-end mt-4">
            <Input
              label={t("Ajouter un numero")}
              placeholder={t("ex: +33612345678")}
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddNumber();
              }}
              size="sm"
              className="flex-1"
              variant="bordered"
              classNames={{ input: "text-sm", label: "text-xs font-medium" }}
            />
            <Button
              size="sm"
              color="primary"
              variant="flat"
              onClick={handleAddNumber}
              isIconOnly
              isDisabled={!newNumber.trim() || numbers.includes(newNumber.trim())}
            >
              <LuPlus size={18} />
            </Button>
          </div>

          {numbers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <BsTelephoneX className="w-10 h-10 mb-3" />
              <span className="text-sm">{t("Aucun numero blackliste")}</span>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-4">
              {numbers.map((number) => (
                <Chip
                  key={number}
                  variant="flat"
                  color="danger"
                  onClose={() => handleRemoveNumber(number)}
                  classNames={{ base: "py-4", content: "text-sm font-mono" }}
                >
                  {number}
                </Chip>
              ))}
            </div>
          )}
        </>
      )}

      {isDirty && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-3 bg-[#171717] text-white rounded-xl px-4 py-3 shadow-lg">
          <span className="text-sm">{t("Unsaved changes")}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              className="bg-white/10 text-white"
              onClick={handleCancel}
              isDisabled={isSaving}
            >
              {t("Annuler")}
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t("Sauvegarder les modifications")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlacklistPanel;
